/**
 * PDP sticky add-to-cart bar.
 *
 * The bar owns its own markup (see snippets/pdp-sticky-cart.liquid) instead of
 * cloning the product info like Madrid's <floated-form>. It stays in sync with
 * the real picker in the info column:
 *
 *   - clicking a value proxies the click to the matching radio in
 *     #variant-radios-<sectionId>, so <variant-radios> does all the work;
 *   - a MutationObserver on #ProductInfo-<sectionId> mirrors price,
 *     availability, swatch colour and add-to-cart state back into the bar;
 *   - visibility is driven by the main buy buttons leaving the viewport and by
 *     the footer entering it.
 */
if (!customElements.get("pdp-sticky-cart")) {
  customElements.define(
    "pdp-sticky-cart",
    class PdpStickyCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;

        this.priceTarget = this.querySelector("[data-sticky-price]");
        this.addButton = this.querySelector("[data-sticky-add]");
        this.addButtonText = this.querySelector("[data-sticky-add-text]");
        this.values = Array.from(this.querySelectorAll("[data-sticky-value]"));
        this.swatches = Array.from(
          this.querySelectorAll("[data-sticky-swatch]")
        );

        this.onClick = this.onClick.bind(this);
        this.onDocumentChange = this.onDocumentChange.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);

        this.addEventListener("click", this.onClick);
        document.addEventListener("change", this.onDocumentChange);

        this.observeProductInfo();
        this.observeVisibility();
        this.sync();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
        document.removeEventListener("change", this.onDocumentChange);
        this.infoObserver?.disconnect();
        this.anchorObserver?.disconnect();
        this.footerObserver?.disconnect();
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      }

      /* ------------------------------------------------------------ lookups */

      get productInfo() {
        return document.getElementById(`ProductInfo-${this.sectionId}`);
      }

      get variantRadios() {
        return document.getElementById(`variant-radios-${this.sectionId}`);
      }

      fieldsetFor(position) {
        const root = this.variantRadios;
        if (!root) return null;
        return (
          root.querySelector(`fieldset[data-option-position="${position}"]`) ||
          null
        );
      }

      /**
       * Radio ids differ between the theme's two picker branches
       * (`<section>-<pos>-<i>` vs `<section>-<pos>-<i>-<productId>`), so match
       * on the fieldset position plus the value instead.
       */
      inputFor(position, value) {
        const scope = this.fieldsetFor(position) || this.variantRadios;
        if (!scope) return null;
        return (
          Array.from(scope.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === value
          ) || null
        );
      }

      /* ----------------------------------------------------------- selection */

      onClick(event) {
        const button = event.target.closest("[data-sticky-value]");
        if (!button || !this.contains(button)) return;

        const input = this.inputFor(
          button.dataset.optionPosition,
          button.dataset.optionValue
        );
        if (!input || input.checked) return;

        input.click();
        this.scheduleSync();
      }

      onDocumentChange(event) {
        const target = event.target;
        if (!target || target.type !== "radio") return;
        if (!this.variantRadios || !this.variantRadios.contains(target)) return;
        this.scheduleSync();
      }

      /* ---------------------------------------------------------------- sync */

      observeProductInfo() {
        const target = this.productInfo;
        if (!target) return;

        this.infoObserver = new MutationObserver(this.scheduleSync);
        this.infoObserver.observe(target, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class", "disabled", "aria-disabled", "style"],
        });
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
        });
      }

      sync() {
        this.syncPrice();
        this.syncValues();
        this.syncSwatches();
        this.syncButton();
      }

      syncPrice() {
        if (!this.priceTarget) return;
        const source = document.querySelector(`#price-${this.sectionId} .price`);
        if (!source) return;
        if (this.priceTarget.innerHTML !== source.outerHTML) {
          this.priceTarget.innerHTML = source.outerHTML;
        }
      }

      syncValues() {
        this.values.forEach((button) => {
          const input = this.inputFor(
            button.dataset.optionPosition,
            button.dataset.optionValue
          );
          if (!input) return;

          button.classList.toggle("is-selected", input.checked);
          button.setAttribute("aria-pressed", input.checked ? "true" : "false");
          button.classList.toggle(
            "is-unavailable",
            input.classList.contains("disabled")
          );
        });
      }

      syncSwatches() {
        this.swatches.forEach((swatch) => {
          const fieldset = this.fieldsetFor(swatch.dataset.optionPosition);
          if (!fieldset) return;

          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (!checked) return;

          const label =
            (checked.id &&
              fieldset.querySelector(
                `label[for="${CSS.escape(checked.id)}"]`
              )) ||
            checked.nextElementSibling;
          if (!label) return;

          const styles = getComputedStyle(label);
          const color = styles.getPropertyValue("--swatch-color").trim();
          if (color) swatch.style.setProperty("--sticky-swatch", color);

          const image = styles.backgroundImage;
          swatch.style.backgroundImage =
            image && image !== "none" ? image : "";

          swatch.title = checked.value;
          const name = this.querySelector("[data-sticky-swatch-name]");
          if (name) name.textContent = checked.value;
        });
      }

      syncButton() {
        if (!this.addButton) return;

        const form = document.getElementById(this.formId);
        const source = form && form.querySelector(".product-form__submit");
        if (!source) return;

        const disabled =
          source.hasAttribute("disabled") ||
          source.getAttribute("aria-disabled") === "true";

        this.addButton.disabled = disabled;
        this.addButton.setAttribute("aria-disabled", disabled ? "true" : "false");
        this.addButton.classList.toggle(
          "loading",
          source.classList.contains("loading")
        );

        const label = source.querySelector("span");
        const text = label && label.textContent.trim();
        if (
          this.addButtonText &&
          text &&
          this.addButtonText.textContent.trim() !== text
        ) {
          this.addButtonText.textContent = text;
        }
      }

      /* ---------------------------------------------------------- visibility */

      observeVisibility() {
        this.pastAnchor = false;
        this.atFooter = false;

        const anchor =
          (this.productInfo &&
            this.productInfo.querySelector(".product__buy_buttons")) ||
          document.querySelector(".product__buy_buttons");

        if (anchor) {
          this.anchorObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.pastAnchor =
                  !entry.isIntersecting && entry.boundingClientRect.top < 0;
              });
              this.updateVisibility();
            },
            { threshold: 0 }
          );
          this.anchorObserver.observe(anchor);
        }

        const footer = document.querySelector("footer");
        if (footer) {
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.atFooter = entry.isIntersecting;
              });
              this.updateVisibility();
            },
            { threshold: 0 }
          );
          this.footerObserver.observe(footer);
        }
      }

      updateVisibility() {
        this.classList.toggle(
          "is-visible",
          this.pastAnchor && !this.atFooter
        );
      }
    }
  );
}
